import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router-dom';
import { useStockMovements } from '~/api/stock-movements';
import { AuthGate } from '~/components/AuthGate';
import { Split } from '~/components/Split';
import { StockMovementTable } from '~/components/stock/stock-movements/StockMovementTable';
import { FilterGroupSearch } from '~/components/ui/FilterGroupSearch';
import { Result } from '~/components/ui/Result';
import { StatefulView } from '~/components/ui/StatefulView';
import { VirtualList } from '~/components/ui/VirtualList';
import { View, ViewSwitcher } from '~/components/view-switcher/ViewSwitcher';
import {
  LocationType,
  useLocationsQuery,
  useStockMovementFiltersQuery,
} from '~/generated/graphql';
import { formatDate } from '~/helpers/formatDate';
import { useDisplayState } from '~/hooks/useDisplayState';
import { useSearch } from '~/hooks/useSearch';
import { StockNav } from '~/layouts/nav/StockNav';
import { StockMovement } from '~/types';

const SEARCH_OPTIONS = {
  keys: [
    'locationType',
    'location',
    'type',
    'sku',
    'enteredBy',
    'locationName',
    'skuName',
    'skuCode',
    'spaceName',
  ],
};

export const StockMovements = () => {
  const { t } = useTranslation();
  const [view, setView] = useState<View>('CARD');
  const [query, setQuery] = useState(new URLSearchParams());
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const isInitialLoadRef = useRef(true);

  const { data, isValidating, error } = useStockMovements(query.toString(), {
    revalidateOnFocus: false,
  });

  const {
    results,
    query: searchValue,
    search,
  } = useSearch(data, SEARCH_OPTIONS);

  const match = useMatch('/stock-movements/:id/*');

  // Main loading state - isValidating from useStockMovements is now primary
  // const isAPILoading = (data === undefined && !error) || isValidating; // Simplified/replaced by isValidating for the hook

  // Ref to track initial load completion - This specific one is for table rendering skeleton
  const initialTableRenderLoadComplete = useRef(false);
  // Debounced state for table rendering
  const [isTableRendering, setIsTableRendering] = useState(false);
  const renderTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing timeout
    if (renderTimeoutRef.current) {
      clearTimeout(renderTimeoutRef.current);
    }

    // Logic for table skeleton rendering (separate from displayState loading)
    if (
      !isValidating &&
      data !== undefined &&
      !initialTableRenderLoadComplete.current
    ) {
      initialTableRenderLoadComplete.current = true;
      setIsTableRendering(true);
      renderTimeoutRef.current = setTimeout(() => {
        setIsTableRendering(false);
      }, 300);
    } else if (isValidating) {
      setIsTableRendering(false);
      if (data === undefined) {
        initialTableRenderLoadComplete.current = false;
      }
    }

    // Cleanup timeout
    return () => {
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
      }
    };
  }, [isValidating, data]);

  const movements = results ?? [];
  const areFiltersActive = Array.from(query.keys()).length > 0;

  // Use the new useDisplayState hook
  const displayState = useDisplayState({
    isValidating,
    data, // Raw data from API
    error,
    derivedData: movements, // Data after search
    isInitialLoadRef,
  });

  const isSearchResultEmpty = useMemo(() => {
    return (
      displayState === 'NO_RESULTS' &&
      !!searchValue &&
      !!data &&
      data.length > 0
    );
  }, [displayState, searchValue, data]);

  const listProps = {
    data: movements,
    getListItemProps,
    scrollTo: match?.params.id ? parseInt(match.params.id) : undefined,
  };

  return (
    <AuthGate action='manage' subject='feat.items'>
      <Split
        mobileAction='none'
        nav={<StockNav />}
        filters={
          <div className='flex w-full items-center gap-2'>
            <ViewSwitcher selected={view} onSelect={setView} />
            <StockMovementFilters
              searchValue={searchValue}
              filtersValue={query}
              onFiltered={setQuery}
              onSearch={search}
            />
            <div className='ml-auto hidden items-center justify-between lg:flex'>
              <div className='whitespace-nowrap text-right'>
                <a
                  className='text-brand hover:underline'
                  href={`${
                    process.env.NODE_ENV === 'development'
                      ? 'http://localhost:4000'
                      : ''
                  }/api/export-movements?${query}`}
                >
                  {t('export')}
                </a>
              </div>
            </div>
          </div>
        }
        disableMainScroll={view === 'TABLE'}
        hasBackground={view === 'TABLE'}
        main={
          <StatefulView
            className={classNames(view === 'TABLE' ? 'h-full' : '')}
            displayState={displayState}
            activeFilters={areFiltersActive}
            isSearchResultEmpty={isSearchResultEmpty}
            entityName='stock movements'
          >
            {movements.length !== undefined && (
              <div className='px-4 pb-3 pt-1'>
                <Result count={movements.length} />
              </div>
            )}
            {view === 'CARD' ? (
              <VirtualList noPadding {...listProps} />
            ) : (
              <div
                ref={tableContainerRef}
                className='h-full w-full flex-grow overflow-auto'
              >
                <StockMovementTable
                  data={movements}
                  isLoading={isTableRendering}
                  containerRef={tableContainerRef}
                />
              </div>
            )}
          </StatefulView>
        }
        context={{ movements }}
      ></Split>
    </AuthGate>
  );
};

function useLocationOptions() {
  const [result] = useLocationsQuery({
    variables: { type: LocationType.Site },
    requestPolicy: 'cache-and-network',
  });
  const { data } = result;
  return data?.locations
    ? data.locations
        .filter((loc) => loc && loc.id && loc.name)
        .map(({ id, name }) => ({ value: `s:${id}`, label: name }))
    : [];
}

const StockMovementFilters = (props: {
  searchValue: string;
  filtersValue: URLSearchParams;
  onSearch: (value: string) => void;
  onFiltered: (value: URLSearchParams) => void;
}) => {
  const { t } = useTranslation();

  const locationOptions = useLocationOptions();
  const [result] = useStockMovementFiltersQuery();
  const { data } = result;

  const movementTypeOptions = useMemo(
    () =>
      data?.movementTypes
        ?.filter((value) => !!value)
        .map((value) => ({
          value,
          label: value,
        })),
    [data]
  );

  const spaces = useMemo(
    () =>
      data?.spaceNames
        ?.filter((value) => !!value)
        .map((value) => ({ value, label: value })),
    [data]
  );

  const skus = useMemo(
    () =>
      data?.items?.flatMap((item) =>
        item && item.skus
          ? item.skus
              .filter((sku) => sku && sku.id && sku.name)
              .map(({ id, name }) => ({ value: id, label: name }))
          : []
      ) ?? [], // Ensure skus is always an array
    [data]
  );

  const userOptions = data?.users?.filter(({ value }) => Boolean(value));

  return (
    <FilterGroupSearch
      filters={[
        {
          type: 'select',
          name: 'location',
          label: t('location'),
          options: locationOptions,
          multiple: false,
        },
        {
          type: 'select',
          name: 'space',
          label: t('space'),
          options: spaces ?? [],
        },
        {
          type: 'select',
          name: 'sku',
          label: t('sku'),
          options: skus ?? [],
        },
        {
          type: 'select',
          name: 'type',
          label: t('movementType'),
          options: movementTypeOptions ?? [],
        },
        {
          type: 'select',
          name: 'enteredBy',
          label: t('enteredBy'),
          options: userOptions ?? [],
        },
        {
          type: 'date',
          name: 'occurred', // Will expand to `occurredAfter` and `occurredBefore`
          label: t('occurredBetween'),
        },
      ]}
      value={{
        searchValue: props.searchValue,
        filtersValue: props.filtersValue,
      }}
      onChange={({ searchValue, filtersValue }) => {
        props.onSearch(searchValue);
        props.onFiltered(filtersValue);
      }}
      placeholder={'Find Stock Movements...'}
    />
  );
};

const getListItemProps = (
  movement: StockMovement,
  scrollTo?: number | string | null
) => ({
  id: movement.id,
  linkTo: `/stock-movements/${movement.id}`,
  image: movement.image,
  titleText: movement.skuName,
  secondaryText: movement.skuCode ?? undefined,
  tertiaryText: <Location movement={movement} />,
  heroElement: (
    <div className='flex items-center gap-3 text-right'>
      <div className='text-sm leading-tight xl:pr-2'>
        {movement.type}
        <br />
        {formatDate(movement.occurredAt, 'P HH:mm')}
      </div>
      {movement.quantity && (
        <div className='mb-0.5 text-xl'>
          {movement.quantity > 0 ? `+${movement.quantity}` : movement.quantity}
        </div>
      )}
    </div>
  ),
  active: Boolean(scrollTo && '' + scrollTo === movement.id.toString()),
});

const Location = ({ movement }: { movement: StockMovement }) => {
  const { t } = useTranslation();

  return (
    <>
      {movement.locationName} ({t('type.' + movement.locationType)}){' > '}
      {movement.spaceName}
    </>
  );
};
